/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IResourceInfo, IView, IViewEditable, IViewInfo, IViewResource } from "../../../view/IView";
import { IEditor } from "../../../IEditor";
import { IntegrationService } from "../../../integration/IntegrationService";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	removeAllEventListeners,
	TGetSimpleEventEmitterDataType,
	TSimpleEventListener
} from "@adapptio/lib-shared";
import { IResourceDescription } from "@adapptio/lib-core";
import { IntegrationModel } from "../Common/IntegrationModel";
import { ResourceService } from "../../../resources/ResourceService";
import { IEditorResource_Base, IEditorResource_View } from "../../../resources/ResourceTypes";
import { ViewService } from "../../../view/ViewService";
import { LayoutState } from "../../../ui/components/Layout/LayoutState";
import { IntegrationEditorEditLayoutConfig } from "./Layout";
import { DialogService } from "../../../dialogs/DialogService";
import { ConfirmationDialog } from "../../../dialogs/ConfirmationDialog/ConfirmationDialog";
import { termsEditorCommon } from "../../../terms/common";
import { ACTION_BUTTON_STYLE } from "../../../ui/components/ActionButton";
import { IIntegrationType } from "../../../integration/IntegrationServiceTypes";
import { ABILITY_ACTIONS, AbilityService } from "../../../ability/AbilityService";

/**
 * View parameters
 */
interface IIntegrationEditorEditParams {
	resourceId: string;
}

/**
 * Error Log View
 */
export class IntegrationEditorEdit
	implements IView<IIntegrationEditorEditParams>, IViewEditable, IViewResource
{
	/** Services */
	private integrationSvc: IntegrationService;
	private abilitySvc: AbilityService;
	private resourceSvc: ResourceService;
	private viewSvc: ViewService;
	private dialogSvc: DialogService;

	/* Resource */
	private resourceDescriptor: IResourceDescription;
	private resource: IEditorResource_View;

	/** View info change event */
	public onViewInfoChanged = createEventEmitter<void>();

	/** Emitted when resource descriptor changed */
	public onResourceInfoChanged = createEventEmitter<void>();

	/* Document model */
	private integrationModel: IntegrationModel;

	/** Layout state */
	private layout = new LayoutState(IntegrationEditorEditLayoutConfig);

	/** If resource was modified */
	public isModified: boolean;

	/** If the view is being saved */
	private saveInProgress = false;

	/* Resource event handlers */
	private handleResourceChange: () => void;
	private handleResourceDelete: TSimpleEventListener<
		TGetSimpleEventEmitterDataType<IEditorResource_Base["onDelete"]>
	>;

	/**
	 * View constructor
	 *
	 * @param dc Dependency container
	 */
	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	public constructor(private dc: IEditor) {
		this.abilitySvc = dc.get("abilityService");
		this.integrationSvc = dc.get("integrationService");
		this.resourceSvc = dc.get("resourceService");
		this.viewSvc = dc.get("viewService");
		this.dialogSvc = dc.get("dialogService");

		this.integrationModel = new IntegrationModel(this.integrationSvc);
	}

	/**
	 * View init
	 *
	 * @param params Params
	 */
	public async init(params: IIntegrationEditorEditParams, viewId: string): Promise<void> {
		this.resourceDescriptor = this.resourceSvc.getResourceDescriptorById(params.resourceId);
		this.resource = (await this.resourceSvc.getBlueprintById(params.resourceId)) as IEditorResource_View;

		emitEvent(this.onViewInfoChanged);

		if (!this.resource) {
			throw new Error(`Resource '${params.resourceId}' was not found.`);
		}

		this.integrationModel = new IntegrationModel(this.integrationSvc);
		await this.integrationModel.loadDocument(this.resource.content);

		onEvent(this.integrationModel.onModelChange, () => {
			this.setIsModified(true, true);
		});

		onEvent(
			this.resource.onChange,
			(this.handleResourceChange = () => {
				this.resourceDescriptor = this.resource.descriptor;
				emitEvent(this.onViewInfoChanged);
				emitEvent(this.onResourceInfoChanged);
			})
		);

		onEvent(
			this.resource.onDelete,
			(this.handleResourceDelete = ({ deletedByLocalAction }) => {
				if (deletedByLocalAction || !this.isModified) {
					this.isModified = false;
					this.viewSvc.close(viewId);
				}
			})
		);
	}

	private emitViewInfoChanged(): void {
		emitEvent(this.onViewInfoChanged);
	}

	/**
	 * Disposes view
	 */
	public dispose(): void {
		if (this.integrationModel) {
			this.integrationModel.dispose();
		}

		removeAllEventListeners(this.onViewInfoChanged);

		if (this.resource) {
			offEvent(this.resource.onChange, this.handleResourceChange);
			offEvent(this.resource.onDelete, this.handleResourceDelete);
		}

		this.layout.dispose();
	}

	private setIsModified(isModified: boolean, notify: boolean) {
		this.isModified = isModified;

		if (notify) {
			emitEvent(this.onViewInfoChanged);
		}
	}

	private setSaveInProgress(saveInProgress: boolean) {
		this.saveInProgress = saveInProgress;
		emitEvent(this.onResourceInfoChanged);
	}

	public getIntegrationModel(): IntegrationModel {
		return this.integrationModel;
	}

	public getIntegrationType(): IIntegrationType {
		return this.integrationSvc.getIntegrationType(this.integrationModel?.getModel().props?.spec?.name);
	}

	/**
	 * Saves changes
	 * Returns false if save is already in progress or no changes were made.
	 */
	public async save(): Promise<boolean> {
		if (!this.resource) {
			throw new Error("Resource was not loaded.");
		}

		if (!this.isModified || this.saveInProgress) {
			return false;
		}

		const content = this.integrationModel.exportDocumentAsYAML();

		this.setSaveInProgress(true);

		this.setIsModified(false, false);

		try {
			await this.resourceSvc.saveBlueprint(
				this.resource.id,
				content,
				this.resource.metaData.revision,
				"Editor Save"
			);

			this.setIsModified(this.isModified, true);
		} catch (err) {
			this.setIsModified(true, true);
			throw err;
		} finally {
			this.setSaveInProgress(false);
		}

		return true;
	}

	/**
	 * Returns layout state
	 */
	public getLayout(): LayoutState {
		return this.layout;
	}

	/**
	 * Returns if a view is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	public canSave(): boolean {
		return (
			this.isModified &&
			!this.isBeingSaved() &&
			this.abilitySvc.can(ABILITY_ACTIONS.WRITE, this.resource)
		);
	}

	/**
	 * Returns if undo is available
	 */
	public canUndo(): boolean {
		return this.integrationModel.undoRedoManager.canUndo();
	}

	/**
	 * Returns if redo is available
	 */
	public canRedo(): boolean {
		return this.integrationModel.undoRedoManager.canRedo();
	}

	/**
	 * Performs undo
	 */
	public undo(): void {
		this.integrationModel.undoRedoManager.undo();
	}

	/**
	 * Performs redo
	 */
	public redo(): void {
		this.integrationModel.undoRedoManager.redo();
	}

	public confirmConfigDelete(): Promise<void> {
		return new Promise((resolve, reject) => {
			this.dialogSvc
				.open(ConfirmationDialog, "confirm-integration-config-delete", {
					title: termsEditorCommon.dialog.deleteIntegrationConfig.title,
					message: termsEditorCommon.dialog.deleteIntegrationConfig.message,
					icon: "mdi/alert",
					confirmLabel: termsEditorCommon.dialog.deleteIntegrationConfig.confirmButton,
					confirmIcon: "mdi/delete",
					confirmStyle: ACTION_BUTTON_STYLE.DANGER,
					cancelStyle: ACTION_BUTTON_STYLE.DEFAULT,
					doConfirm: async () => {
						resolve();
					}
				})
				.then(() => {
					reject();
				})
				.catch((err) => {
					reject(err);
				});
		});
	}

	/**
	 * Returns resource info
	 */
	public getResourceInfo(): IResourceInfo {
		return {
			resource: this.resource,
			parentList: this.resourceSvc.getResourceParentList(this.resource.id),
			isBeingSaved: this.isBeingSaved()
		};
	}

	/**
	 * Returns view info
	 */
	public getViewInfo(): IViewInfo {
		return {
			label: this.resourceDescriptor?.name || "...",
			icon: "mdi/connection",
			canClose: true,
			canSave: this.canSave(),
			isModified: this.isModified,
			renderWhenNotActive: true,
			tabStyleModifier: {
				"resource-type-integration": true
			}
		};
	}
}
